import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, X, Upload, Link, PlusCircle } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

const AdminTours = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState('');
  const [useUrlInput, setUseUrlInput] = useState(true);
  
  const [itineraryDays, setItineraryDays] = useState([]);
  const [galleryImages, setGalleryImages] = useState([]);
  const [whatToBringItems, setWhatToBringItems] = useState([]);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    duration: '',
    price: '',
    image_url: '',
    included: [],
    featured: false,
    meeting_point: '',
    cancelation_policy: '',
    best_time: ''
  });

  useEffect(() => {
    fetchTours();
  }, []);

  const fetchTours = async () => {
    const token = localStorage.getItem('adminToken');
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/tours`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTours(response.data);
    } catch (error) {
      console.error('Error fetching tours:', error);
      alert('Failed to fetch tours');
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API_BASE_URL}/api/admin/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        setFormData(prev => ({ ...prev, image_url: response.data.imageUrl }));
        setImagePreview(response.data.imageUrl);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleGalleryUpload = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('image', file);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await axios.post(`${API_BASE_URL}/api/admin/upload`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.data.success) {
        const newGallery = [...galleryImages];
        newGallery[index] = response.data.imageUrl;
        setGalleryImages(newGallery);
      }
    } catch (error) {
      console.error('Upload error:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const addItineraryDay = () => {
    setItineraryDays([...itineraryDays, '']);
  };

  const removeItineraryDay = (index) => {
    const newDays = itineraryDays.filter((_, i) => i !== index);
    setItineraryDays(newDays);
  };

  const updateItineraryDay = (index, value) => {
    const newDays = [...itineraryDays];
    newDays[index] = value;
    setItineraryDays(newDays);
  };

  const addGalleryImage = () => {
    setGalleryImages([...galleryImages, '']);
  };

  const removeGalleryImage = (index) => {
    const newGallery = galleryImages.filter((_, i) => i !== index);
    setGalleryImages(newGallery);
  };

  const updateGalleryImage = (index, value) => {
    const newGallery = [...galleryImages];
    newGallery[index] = value;
    setGalleryImages(newGallery);
  };

  const addWhatToBringItem = () => {
    setWhatToBringItems([...whatToBringItems, '']);
  };

  const removeWhatToBringItem = (index) => {
    const newItems = whatToBringItems.filter((_, i) => i !== index);
    setWhatToBringItems(newItems);
  };

  const updateWhatToBringItem = (index, value) => {
    const newItems = [...whatToBringItems];
    newItems[index] = value;
    setWhatToBringItems(newItems);
  };

  const addIncludedItem = () => {
    setFormData({
      ...formData,
      included: [...formData.included, '']
    });
  };

  const removeIncludedItem = (index) => {
    setFormData({
      ...formData,
      included: formData.included.filter((_, i) => i !== index)
    });
  };

  const updateIncludedItem = (index, value) => {
    const newIncluded = [...formData.included];
    newIncluded[index] = value;
    setFormData({ ...formData, included: newIncluded });
  };

  const safeParseJSON = (data, defaultValue = []) => {
    if (!data) return defaultValue;
    if (Array.isArray(data)) return data;
    if (typeof data === 'string') {
      try {
        const parsed = JSON.parse(data);
        return Array.isArray(parsed) ? parsed : defaultValue;
      } catch (e) {
        return defaultValue;
      }
    }
    return defaultValue;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('adminToken');
    const config = { headers: { Authorization: `Bearer ${token}` } };

    const submitData = {
      name: formData.name || '',
      description: formData.description || '',
      duration: formData.duration || '',
      price: formData.price || 'Price on Request',
      image_url: formData.image_url || '',
      included: formData.included.filter(item => item.trim() !== ''),
      featured: formData.featured || false,
      itinerary: itineraryDays.filter(day => day.trim() !== ''),
      gallery: galleryImages.filter(url => url.trim() !== ''),
      meeting_point: formData.meeting_point || '',
      cancelation_policy: formData.cancelation_policy || '',
      what_to_bring: whatToBringItems.filter(item => item.trim() !== ''),
      best_time: formData.best_time || ''
    };

    try {
      let response;
      if (editingTour) {
        response = await axios.put(`${API_BASE_URL}/api/admin/tours/${editingTour.id}`, submitData, config);
      } else {
        response = await axios.post(`${API_BASE_URL}/api/admin/tours`, submitData, config);
      }
      
      if (response.data.success) {
        fetchTours();
        setShowModal(false);
        resetForm();
        alert(editingTour ? 'Tour updated successfully!' : 'Tour added successfully!');
      }
    } catch (error) {
      console.error('Error saving tour:', error);
      alert(error.response?.data?.error || 'Error saving tour. Please try again.');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this tour?')) {
      const token = localStorage.getItem('adminToken');
      try {
        await axios.delete(`${API_BASE_URL}/api/admin/tours/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchTours();
        alert('Tour deleted successfully!');
      } catch (error) {
        console.error('Error deleting tour:', error);
        alert('Error deleting tour. Please try again.');
      }
    }
  };

  const handleEdit = (tour) => {
    setEditingTour(tour);
    
    const parsedItinerary = safeParseJSON(tour.itinerary, ['']);
    const parsedGallery = safeParseJSON(tour.gallery, ['']);
    const parsedWhatToBring = safeParseJSON(tour.what_to_bring, ['']);
    const parsedIncluded = safeParseJSON(tour.included, []);
    
    setItineraryDays(parsedItinerary.length ? parsedItinerary : []);
    setGalleryImages(parsedGallery.length ? parsedGallery : []);
    setWhatToBringItems(parsedWhatToBring.length ? parsedWhatToBring : []);
    
    setFormData({
      name: tour.name || '',
      description: tour.description || '',
      duration: tour.duration || '',
      price: tour.price || '',
      image_url: tour.image_url || '',
      included: parsedIncluded,
      featured: tour.featured || false,
      meeting_point: tour.meeting_point || '',
      cancelation_policy: tour.cancelation_policy || '',
      best_time: tour.best_time || ''
    });
    setImagePreview(tour.image_url || '');
    setShowModal(true);
    setUseUrlInput(true);
  };

  const resetForm = () => {
    setEditingTour(null);
    setFormData({
      name: '',
      description: '',
      duration: '',
      price: '',
      image_url: '',
      included: [],
      featured: false,
      meeting_point: '',
      cancelation_policy: '',
      best_time: ''
    });
    setItineraryDays([]);
    setGalleryImages([]);
    setWhatToBringItems([]);
    setImagePreview('');
    setUseUrlInput(true);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="w-10 h-10 border-3 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-white">Manage Tours</h1>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add New Tour</span>
        </button>
      </div>

      {/* Tours Table */}
      <div className="bg-dark-900 rounded-lg overflow-hidden border border-dark-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-dark-800">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">ID</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Image</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Name</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Duration</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Price</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Featured</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {tours.map((tour) => (
                <tr key={tour.id} className="hover:bg-dark-800 transition">
                  <td className="px-4 py-3 text-sm text-gray-400">{tour.id}</td>
                  <td className="px-4 py-3">
                    <img src={tour.image_url} alt={tour.name} className="w-12 h-12 object-cover rounded" />
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-white">{tour.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">{tour.duration}</td>
                  <td className="px-4 py-3 text-sm text-gray-400">
                    {typeof tour.price === 'number' ? tour.price.toLocaleString() : tour.price} RWF
                  </td>
                  <td className="px-4 py-3">
                    {tour.featured ? (
                      <span className="bg-orange-500/20 text-orange-400 px-2 py-1 rounded-full text-xs">Yes</span>
                    ) : (
                      <span className="bg-dark-700 text-gray-400 px-2 py-1 rounded-full text-xs">No</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex space-x-2">
                      <button onClick={() => handleEdit(tour)} className="text-blue-400 hover:text-blue-300">
                        <Edit2 size={18} />
                      </button>
                      <button onClick={() => handleDelete(tour.id)} className="text-red-400 hover:text-red-300">
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-dark-800 rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto border border-dark-700">
            <div className="p-4 border-b border-dark-700 flex justify-between items-center sticky top-0 bg-dark-800">
              <h2 className="text-xl font-bold text-white">
                {editingTour ? 'Edit Tour' : 'Add New Tour'}
              </h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Tour Name *</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Description *</label>
                <textarea
                  required
                  rows="3"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg focus:outline-none focus:border-orange-500 text-white"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Duration *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g., 2 Days"
                    value={formData.duration}
                    onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Price *</label>
                  <input
                    type="text"
                    required
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg"
                    placeholder="e.g., 1,500,000 RWF or Price on Request"
                  />
                </div>
              </div>

              {/* Main Image */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Main Tour Image</label>
                <div className="flex space-x-2 mb-3">
                  <button
                    type="button"
                    onClick={() => setUseUrlInput(true)}
                    className={`flex-1 py-2 rounded-lg text-sm ${useUrlInput ? 'bg-orange-500 text-white' : 'bg-dark-700 text-gray-400'}`}
                  >
                    Use URL
                  </button>
                  <button
                    type="button"
                    onClick={() => setUseUrlInput(false)}
                    className={`flex-1 py-2 rounded-lg text-sm ${!useUrlInput ? 'bg-orange-500 text-white' : 'bg-dark-700 text-gray-400'}`}
                  >
                    Upload File
                  </button>
                </div>
                {useUrlInput ? (
                  <input
                    type="text"
                    value={formData.image_url}
                    onChange={(e) => {
                      setFormData({ ...formData, image_url: e.target.value });
                      setImagePreview(e.target.value);
                    }}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                    placeholder="https://example.com/image.jpg"
                  />
                ) : (
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white file:mr-2 file:py-1 file:px-3 file:rounded file:bg-orange-500 file:text-white"
                    disabled={uploading}
                  />
                )}
                {uploading && <p className="text-gray-400 text-sm mt-1">Uploading...</p>}
                {imagePreview && (
                  <img src={imagePreview} alt="Preview" className="mt-3 w-24 h-24 object-cover rounded" />
                )}
              </div>

              {/* Gallery Images */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Gallery Images</label>
                {Array.isArray(galleryImages) && galleryImages.map((image, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={image || ''}
                      onChange={(e) => updateGalleryImage(index, e.target.value)}
                      placeholder="Image URL"
                      className="flex-1 p-2 bg-dark-700 border border-dark-600 rounded-lg text-white text-sm"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleGalleryUpload(e, index)}
                      className="hidden"
                      id={`gallery-upload-${index}`}
                    />
                    <label
                      htmlFor={`gallery-upload-${index}`}
                      className="px-3 py-2 bg-dark-700 rounded-lg hover:bg-dark-600 cursor-pointer text-white text-sm"
                    >
                      <Upload size={16} />
                    </label>
                    {galleryImages.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(index)}
                        className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addGalleryImage}
                  className="mt-2 text-orange-500 hover:text-orange-400 text-sm"
                >
                  + Add another image
                </button>
              </div>

              {/* Itinerary */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">Itinerary (Day by Day)</label>
                {Array.isArray(itineraryDays) && itineraryDays.map((day, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <span className="text-orange-500 pt-2">Day {index + 1}:</span>
                    <input
                      type="text"
                      value={day || ''}
                      onChange={(e) => updateItineraryDay(index, e.target.value)}
                      placeholder="Describe activities"
                      className="flex-1 p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                    />
                    {itineraryDays.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeItineraryDay(index)}
                        className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addItineraryDay}
                  className="mt-2 text-orange-500 hover:text-orange-400 text-sm"
                >
                  + Add another day
                </button>
              </div>

              {/* What's Included */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">What's Included</label>
                {Array.isArray(formData.included) && formData.included.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={item || ''}
                      onChange={(e) => updateIncludedItem(index, e.target.value)}
                      placeholder="e.g., Park permits"
                      className="flex-1 p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                    />
                    <button
                      type="button"
                      onClick={() => removeIncludedItem(index)}
                      className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addIncludedItem}
                  className="mt-2 text-orange-500 hover:text-orange-400 text-sm"
                >
                  + Add included item
                </button>
              </div>

              {/* What to Bring */}
              <div>
                <label className="block text-sm font-medium mb-2 text-gray-300">What to Bring</label>
                {Array.isArray(whatToBringItems) && whatToBringItems.map((item, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={item || ''}
                      onChange={(e) => updateWhatToBringItem(index, e.target.value)}
                      placeholder="e.g., Hiking boots"
                      className="flex-1 p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                    />
                    {whatToBringItems.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeWhatToBringItem(index)}
                        className="px-3 py-2 bg-red-500/20 text-red-400 rounded-lg"
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addWhatToBringItem}
                  className="mt-2 text-orange-500 hover:text-orange-400 text-sm"
                >
                  + Add item
                </button>
              </div>

              {/* Additional Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Meeting Point</label>
                  <input
                    type="text"
                    value={formData.meeting_point || ''}
                    onChange={(e) => setFormData({ ...formData, meeting_point: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                    placeholder="Your hotel in Kigali"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-300">Best Time to Visit</label>
                  <input
                    type="text"
                    value={formData.best_time || ''}
                    onChange={(e) => setFormData({ ...formData, best_time: e.target.value })}
                    className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                    placeholder="June to September"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1 text-gray-300">Cancellation Policy</label>
                <textarea
                  rows="2"
                  value={formData.cancelation_policy || ''}
                  onChange={(e) => setFormData({ ...formData, cancelation_policy: e.target.value })}
                  className="w-full p-2 bg-dark-700 border border-dark-600 rounded-lg text-white"
                  placeholder="Describe your cancellation policy"
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                  className="w-4 h-4 rounded border-dark-600 bg-dark-700 text-orange-500"
                />
                <label htmlFor="featured" className="text-sm text-gray-300">Featured Tour</label>
              </div>

              <div className="flex space-x-3 pt-4">
                <button type="submit" className="flex-1 bg-orange-500 text-white py-2 rounded-lg hover:bg-orange-600 transition">
                  {editingTour ? 'Update' : 'Add'} Tour
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 border border-dark-600 py-2 rounded-lg hover:bg-dark-700 transition text-gray-300">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTours;